import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiService from '../services/apiService';
import { resolveEnv } from '../utils/subdomain';
import './Cenas.css';

// Tela "Cenas" (armazém de cenas SDUI por hash). Contrato: SCENE_STORE_CONTRACT.md do app.
// A tela NÃO ativa nada: quem põe a cena em uso é o config SDUI, apontando o hash.

const MAX_BYTES = 8 * 1024 * 1024;   // teto de uma cena (igual ao servidor)
const ENV_LABEL = { prod: 'Produção', qa: 'QA', hmg: 'Homologação' };

// `created` chega em UTC, ISO sem offset ("2026-10-20T14:05:00.123456"). Não passamos a string
// por new Date(): sem offset o navegador a leria como hora LOCAL (e "…Z" ausente é Invalid Date em
// alguns Safaris). Lemos os componentes numéricos, somamos em UTC e subtraímos 3h (Brasília, sem
// horário de verão desde 2019).
const fmtBrt = (iso) => {
    if (!iso) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
    if (!m) return iso;
    const t = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) - 3 * 3600 * 1000);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(t.getUTCDate())}/${p(t.getUTCMonth() + 1)}/${String(t.getUTCFullYear()).slice(2)} ` +
        `${p(t.getUTCHours())}:${p(t.getUTCMinutes())}`;
};

const fmtBytes = (n) => {
    if (n == null) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const utf8Len = (s) => new TextEncoder().encode(s).length;

const sha256Hex = async (text) => {
    if (!window.crypto?.subtle) throw new Error('Este navegador não oferece Web Crypto (precisa de HTTPS)');
    const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Validação local, espelho leve da do servidor. Devolve lista de erros (vazia = ok).
const validateLocal = (text) => {
    if (!text || !text.trim()) return ['O texto da cena está vazio'];
    if (utf8Len(text) > MAX_BYTES) return [`Cena grande demais: ${utf8Len(text)} bytes (máximo ${MAX_BYTES})`];
    let data;
    try { data = JSON.parse(text); } catch (e) { return [`JSON inválido: ${e.message}`]; }
    if (data === null || typeof data !== 'object' || Array.isArray(data)) return ['A cena deve ser um objeto JSON'];
    const errs = [];
    if (data.v !== 1) errs.push("Campo 'v' deve ser 1");
    if (!Array.isArray(data.elements)) errs.push("Campo 'elements' deve ser uma lista");
    return errs;
};

const errMsg = (e, fallback) => e?.response?.data?.error || e?.message || fallback;

const isDraft = (s) => s.source_repo === 'draft' && s.source_ref === 'draft';

const sourceLabel = (s) => {
    if (isDraft(s)) return 'rascunho';
    if (!s.source_repo && !s.source_ref && !s.source_sha) return 'manual';
    const parts = [s.source_repo, s.source_ref, s.source_sha ? s.source_sha.slice(0, 7) : null].filter(Boolean);
    return parts.join(' @ ');
};

function Cenas({ user }) {
    const env = resolveEnv();
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 5000);
    }, []);

    const [companies, setCompanies] = useState([]);
    const [subdomain, setSubdomain] = useState('core');
    const [scenes, setScenes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState(null);
    const [nameFilter, setNameFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('all');   // all | active | removed
    const [copied, setCopied] = useState(null);

    // Ver JSON
    const [viewer, setViewer] = useState(null);   // { loading, error, data, formatted }
    const closeBtnRef = useRef(null);

    // Visualizador animado (Scene Studio em iframe). `body` é o texto exato da cena.
    const [preview, setPreview] = useState(null);   // { title, hash?, body?, loading, error }
    const frameRef = useRef(null);
    const deepLink = useRef(new URLSearchParams(window.location.search));
    const deepLinkDone = useRef(false);

    // Confirmação dentro da página (a página não usa confirm())
    const [pending, setPending] = useState(null);  // { kind: 'delete'|'restore', scene }
    const [acting, setActing] = useState(false);

    // Subir cena
    const [text, setText] = useState('');
    const [name, setName] = useState('');
    const [localHash, setLocalHash] = useState(null);
    const [validated, setValidated] = useState(null);     // { key, exists, hash, bytes, deleted_before }
    const [busy, setBusy] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [hashWarning, setHashWarning] = useState(null);
    const [published, setPublished] = useState(null);

    // `user` só é usado para o controle de rota (role admin); o user_id das chamadas vem da sessão no servidor.
    void user;

    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) {
                notify('Erro ao carregar empresas', 'error');
            }
        })();
    }, [notify]);

    const load = useCallback(async () => {
        setLoading(true); setListError(null);
        try {
            const r = await apiService.scenesList(subdomain);
            setScenes(Array.isArray(r?.scenes) ? r.scenes : []);
        } catch (e) {
            setScenes([]);
            setListError(errMsg(e, 'Erro ao carregar as cenas'));
        } finally {
            setLoading(false);
        }
    }, [subdomain]);

    useEffect(() => { setPending(null); setViewer(null); load(); }, [load]);

    // Link vindo do MCP (`?sd=<empresa>&hash=<sha256>`): escolhe a empresa uma vez, e depois que a
    // lista dela chega abre a cena já animada.
    useEffect(() => {
        const sd = deepLink.current.get('sd');
        if (sd && !deepLinkDone.current) setSubdomain(sd);
    }, []);

    const rows = useMemo(() => {
        const q = nameFilter.trim().toLowerCase();
        return scenes
            .filter((s) => (stateFilter === 'all' || (stateFilter === 'removed') === !!s.deleted))
            .filter((s) => !q || (s.name || '').toLowerCase().includes(q))
            .slice()
            .sort((a, b) => (a.created < b.created ? 1 : a.created > b.created ? -1 : 0));
    }, [scenes, nameFilter, stateFilter]);

    const copyHash = async (hash) => {
        try {
            await navigator.clipboard.writeText(hash);
            setCopied(hash);
            setTimeout(() => setCopied(null), 1500);
        } catch (e) { notify('Não foi possível copiar o hash', 'error'); }
    };

    // ── Ver JSON ────────────────────────────────────────────────────────────
    const openViewer = async (scene) => {
        setViewer({ loading: true, scene });
        try {
            const data = await apiService.scenesBody(subdomain, scene.hash);
            let formatted = data.body;
            try { formatted = JSON.stringify(JSON.parse(data.body), null, 2); } catch (e) { /* mostra o texto cru */ }
            setViewer({ loading: false, scene, data, formatted });
        } catch (e) {
            setViewer({ loading: false, scene, error: errMsg(e, 'Erro ao ler a cena') });
        }
    };

    // ── Visualizar animada ──────────────────────────────────────────────────
    const openPreview = async (scene) => {
        setPreview({ title: scene.name, hash: scene.hash, loading: true });
        try {
            await apiService.scenesStudioPass();
            const data = await apiService.scenesBody(subdomain, scene.hash);
            setPreview({ title: scene.name, hash: scene.hash, body: data.body, loading: false });
        } catch (e) {
            setPreview({ title: scene.name, hash: scene.hash, loading: false, error: errMsg(e, 'Erro ao ler a cena') });
        }
    };

    const openLocalPreview = async () => {
        setPreview({ title: name.trim() || 'Pré-visualização', loading: true, local: true });
        try {
            await apiService.scenesStudioPass();
            setPreview({ title: name.trim() || 'Pré-visualização', body: text, loading: false, local: true });
        } catch (e) {
            setPreview({ title: name.trim() || 'Pré-visualização', loading: false, local: true, error: errMsg(e, 'Erro ao abrir o visualizador') });
        }
    };

    useEffect(() => {
        const hash = deepLink.current.get('hash');
        if (!hash || deepLinkDone.current || loading) return;
        const sd = deepLink.current.get('sd');
        if (sd && sd !== subdomain) return;
        const found = scenes.find((s) => s.hash === hash);
        if (found) { deepLinkDone.current = true; openPreview(found); }
        else if (scenes.length || listError) {
            deepLinkDone.current = true;
            notify('A cena do link não está nesta empresa (ou foi retirada).', 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenes, loading, subdomain, listError]);

    // Conversa com o Studio: ele avisa `ready` e recebe a cena. Só aceitamos mensagens do iframe.
    useEffect(() => {
        if (!preview || !preview.body) return undefined;
        let scene;
        try { scene = JSON.parse(preview.body); } catch (e) { return undefined; }
        const send = () => {
            const w = frameRef.current && frameRef.current.contentWindow;
            if (w) w.postMessage({ type: 'scene', scene }, window.location.origin);
        };
        const onMsg = (e) => {
            const w = frameRef.current && frameRef.current.contentWindow;
            if (e.origin !== window.location.origin || e.source !== w) return;
            if (e.data && e.data.source === 'scene-studio' && e.data.type === 'ready') send();
        };
        window.addEventListener('message', onMsg);
        send();   // se o iframe já estava pronto quando o corpo chegou
        return () => window.removeEventListener('message', onMsg);
    }, [preview]);

    useEffect(() => {
        if (!preview) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setPreview(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [preview]);

    useEffect(() => {
        if (!viewer) return undefined;
        if (closeBtnRef.current) closeBtnRef.current.focus();
        const onKey = (e) => { if (e.key === 'Escape') setViewer(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [viewer]);

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(viewer.data.body);   // texto exato, não o formatado
            notify('JSON copiado (texto exato, sem formatação)', 'success');
        } catch (e) { notify('Não foi possível copiar', 'error'); }
    };

    // ── Retirar / Restaurar ─────────────────────────────────────────────────
    const runPending = async () => {
        if (!pending) return;
        setActing(true);
        try {
            if (pending.kind === 'delete') {
                await apiService.scenesDelete({ subdomain, hash: pending.scene.hash });
                notify('Cena retirada', 'success');
            } else {
                const r = await apiService.scenesRestore({ subdomain, hash: pending.scene.hash });
                notify(r?.restored === false ? 'A cena já estava ativa' : 'Cena restaurada', 'success');
            }
            setPending(null);
            await load();
        } catch (e) {
            notify(errMsg(e, 'Erro ao executar a ação'), 'error');
        } finally {
            setActing(false);
        }
    };

    // ── Subir cena ──────────────────────────────────────────────────────────
    const key = `${subdomain}|${name.trim()}|${localHash}`;
    const localErrors = useMemo(() => (text ? validateLocal(text) : []), [text]);

    useEffect(() => {
        let alive = true;
        setValidated(null); setHashWarning(null); setPublished(null); setUploadError(null);
        if (!text || validateLocal(text).length) { setLocalHash(null); return undefined; }
        sha256Hex(text).then((h) => { if (alive) setLocalHash(h); })
            .catch((e) => { if (alive) { setLocalHash(null); setUploadError(e.message); } });
        return () => { alive = false; };
    }, [text]);

    // Qualquer mudança de destino/nome invalida a validação anterior.
    useEffect(() => { setValidated(null); setHashWarning(null); setPublished(null); }, [subdomain, name]);

    const onFile = async (e) => {
        const f = e.target.files && e.target.files[0];
        if (!f) return;
        if (f.size > MAX_BYTES * 1.1) { setUploadError('Arquivo grande demais (máximo 8 MiB)'); return; }
        const content = await f.text();
        setText(content);
        setName(f.name.replace(/\.scene\.json$/i, '').replace(/\.json$/i, ''));
        e.target.value = '';
    };

    const canSend = !!text && localErrors.length === 0 && !!localHash && name.trim().length > 0 && name.trim().length <= 255;

    const validate = async () => {
        setBusy(true); setUploadError(null); setPublished(null); setHashWarning(null);
        try {
            const r = await apiService.scenesPut({ subdomain, name: name.trim(), body: text, dryRun: true });
            setValidated({ key, ...r });
            if (r.hash !== localHash) {
                setHashWarning(`O hash calculado pelo servidor (${r.hash}) é diferente do calculado no navegador (${localHash}). Não publique; avise o time.`);
            }
        } catch (e) {
            setValidated(null);
            setUploadError(errMsg(e, 'Erro ao validar a cena'));
        } finally { setBusy(false); }
    };

    const publish = async () => {
        setBusy(true); setUploadError(null);
        try {
            const r = await apiService.scenesPut({ subdomain, name: name.trim(), body: text, dryRun: false });
            setPublished(r);
            setValidated(null);
            if (r.hash !== localHash) {
                setHashWarning(`O hash devolvido pela rota (${r.hash}) é diferente do calculado no navegador (${localHash}).`);
            }
            await load();
        } catch (e) {
            setUploadError(errMsg(e, 'Erro ao publicar a cena'));
        } finally { setBusy(false); }
    };

    const validOk = validated && validated.key === key && validated.hash === localHash;

    const companyOptions = useMemo(() => {
        const list = companies.filter((c) => c.subdomain && c.subdomain !== 'core');
        return list.sort((a, b) => (a.name || a.subdomain).localeCompare(b.name || b.subdomain));
    }, [companies]);

    return (
        <div className="cn-page">
            {toast && <div className={`cn-toast cn-toast-${toast.type}`} role="status">{toast.msg}</div>}

            <h1 className="cn-title">Cenas</h1>
            <p className="cn-help">
                Cenas do motor de animação do app, guardadas por <strong>hash do conteúdo</strong> (sha256 do texto
                exato). Uma cena não muda: editar o arquivo gera outro hash. Datas em <strong>horário de Brasília</strong>.
            </p>
            <div className="cn-notice" role="note">
                <strong>Ambiente: {ENV_LABEL[env] || env}.</strong>{' '}
                Publicar ou retirar aqui só tem efeito quando o ambiente permite escrita de cenas (a API recusa
                com uma mensagem quando não permite). <strong>Esta tela não ativa nada:</strong> a cena só entra
                em uso quando o config SDUI da empresa aponta para o hash.
            </div>

            <div className="cn-filters">
                <div className="cn-field">
                    <label htmlFor="cn-company">Empresa</label>
                    <select id="cn-company" value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                        <option value="core">core (cenas compartilhadas)</option>
                        {companyOptions.map((c) => (
                            <option key={c.subdomain} value={c.subdomain}>{c.name || c.subdomain}</option>
                        ))}
                    </select>
                </div>
                <div className="cn-field">
                    <label htmlFor="cn-name">Nome</label>
                    <input id="cn-name" type="search" value={nameFilter} placeholder="filtrar por nome"
                        onChange={(e) => setNameFilter(e.target.value)} />
                </div>
                <div className="cn-field">
                    <span id="cn-state-label" className="cn-field-label">Estado</span>
                    <div className="cn-pills" role="group" aria-labelledby="cn-state-label">
                        {[['all', 'Todas'], ['active', 'Ativas'], ['removed', 'Retiradas']].map(([v, label]) => (
                            <button key={v} type="button" className={`cn-pill ${stateFilter === v ? 'active' : ''}`}
                                aria-pressed={stateFilter === v} onClick={() => setStateFilter(v)}>{label}</button>
                        ))}
                    </div>
                </div>
                <div className="cn-filters-actions">
                    <button className="cn-btn" onClick={load} disabled={loading}>{loading ? 'Carregando…' : 'Atualizar'}</button>
                </div>
            </div>

            {listError && <p className="cn-error" role="alert">{listError}</p>}

            <div className="cn-table-wrap">
                <table className="cn-table">
                    <caption className="cn-sr">Cenas de {subdomain}</caption>
                    <thead>
                        <tr>
                            <th scope="col">Nome</th>
                            <th scope="col">Hash</th>
                            <th scope="col" className="cn-num">Tamanho</th>
                            <th scope="col">Criada em</th>
                            <th scope="col">Origem</th>
                            <th scope="col">Estado</th>
                            <th scope="col">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={7} className="cn-empty">
                                {loading ? 'Carregando…' : 'Nenhuma cena encontrada.'}
                            </td></tr>
                        )}
                        {rows.map((s) => (
                            <tr key={s.hash} className={s.deleted ? 'cn-row-removed' : ''}>
                                <td>{s.name}</td>
                                <td>
                                    <code title={s.hash}>{s.hash.slice(0, 10)}</code>{' '}
                                    <button className="cn-btn cn-btn-sm" onClick={() => copyHash(s.hash)}
                                        aria-label={`Copiar hash completo de ${s.name}`}>
                                        {copied === s.hash ? 'Copiado' : 'Copiar'}
                                    </button>
                                </td>
                                <td className="cn-num" title={`${s.bytes} bytes`}>{fmtBytes(s.bytes)}</td>
                                <td>{fmtBrt(s.created)}</td>
                                <td>{isDraft(s)
                                    ? <span className="cn-badge cn-badge-draft" title="Rascunho gravado pelo MCP de QA; nenhum config o referencia">rascunho</span>
                                    : sourceLabel(s)}</td>
                                <td>
                                    <span className={`cn-badge ${s.deleted ? 'cn-badge-removed' : 'cn-badge-active'}`}>
                                        {s.deleted ? 'retirada' : 'ativa'}
                                    </span>
                                </td>
                                <td className="cn-actions">
                                    <button className="cn-btn cn-btn-sm cn-btn-primary" onClick={() => openPreview(s)}>Ver animada</button>
                                    <button className="cn-btn cn-btn-sm" onClick={() => openViewer(s)}>Ver JSON</button>
                                    {s.deleted
                                        ? <button className="cn-btn cn-btn-sm" onClick={() => setPending({ kind: 'restore', scene: s })}>Restaurar</button>
                                        : <button className="cn-btn cn-btn-sm cn-btn-danger" onClick={() => setPending({ kind: 'delete', scene: s })}>Retirar</button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {pending && (
                <div className={`cn-confirm ${pending.kind === 'delete' ? 'cn-confirm-danger' : ''}`} role="alertdialog"
                    aria-labelledby="cn-confirm-title">
                    <p id="cn-confirm-title">
                        {pending.kind === 'delete'
                            ? <>Retirar a cena <strong>{pending.scene.name}</strong> ({pending.scene.hash.slice(0, 10)}) de <strong>{subdomain}</strong>? O servidor não sabe quais configs ainda apontam para ela: se algum apontar, o app cai no aviso do motor de cenas. Dá para restaurar depois.</>
                            : <>Restaurar a cena <strong>{pending.scene.name}</strong> ({pending.scene.hash.slice(0, 10)}) em <strong>{subdomain}</strong>?</>}
                    </p>
                    <div className="cn-confirm-actions">
                        <button className={`cn-btn ${pending.kind === 'delete' ? 'cn-btn-danger' : 'cn-btn-primary'}`}
                            onClick={runPending} disabled={acting}>
                            {acting ? 'Aguarde…' : (pending.kind === 'delete' ? 'Confirmar retirada' : 'Confirmar restauração')}
                        </button>
                        <button className="cn-btn" onClick={() => setPending(null)} disabled={acting}>Cancelar</button>
                    </div>
                </div>
            )}

            {/* ── Subir cena ───────────────────────────────────────────── */}
            <section className="cn-section" aria-labelledby="cn-upload-title">
                <h2 id="cn-upload-title">Subir cena</h2>
                <p className="cn-help">
                    Destino: <strong>{subdomain}</strong> (a empresa escolhida acima). O hash é calculado aqui, sobre o
                    texto exato; a API calcula o dela e os dois têm de bater. Mesmo texto reenviado não duplica.
                </p>
                <div className="cn-upload-grid">
                    <div className="cn-field">
                        <label htmlFor="cn-file">Arquivo .scene.json</label>
                        <input id="cn-file" type="file" accept=".json,application/json" onChange={onFile} />
                    </div>
                    <div className="cn-field">
                        <label htmlFor="cn-scene-name">Nome da cena</label>
                        <input id="cn-scene-name" type="text" value={name} maxLength={255}
                            onChange={(e) => setName(e.target.value)} placeholder="ex.: meta_batida" />
                    </div>
                </div>
                <div className="cn-field">
                    <label htmlFor="cn-text">Ou cole o texto da cena</label>
                    <textarea id="cn-text" rows={8} value={text} spellCheck={false}
                        onChange={(e) => setText(e.target.value)} />
                </div>

                {text && (
                    <ul className="cn-facts">
                        <li>Tamanho: <strong>{fmtBytes(utf8Len(text))}</strong> ({utf8Len(text)} bytes)</li>
                        <li>sha256 (navegador): <code>{localHash || (localErrors.length ? '—' : 'calculando…')}</code></li>
                    </ul>
                )}
                {localErrors.length > 0 && (
                    <ul className="cn-error" role="alert">{localErrors.map((m) => <li key={m}>{m}</li>)}</ul>
                )}
                {uploadError && <p className="cn-error" role="alert">{uploadError}</p>}
                {hashWarning && <p className="cn-error" role="alert"><strong>Atenção:</strong> {hashWarning}</p>}

                {validOk && (
                    <p className="cn-ok" role="status">
                        Validada. Hash {validated.hash.slice(0, 10)}…, {validated.bytes} bytes.{' '}
                        {validated.exists
                            ? (validated.deleted_before ? 'Já existe, está retirada: publicar vai reativá-la.' : 'Já existe e está ativa: publicar não muda nada.')
                            : 'Cena nova.'}
                    </p>
                )}
                {published && (
                    <p className="cn-ok" role="status">
                        {published.created ? 'Cena publicada.' : (published.deleted_before ? 'Cena reativada.' : 'A cena já existia: nada mudou.')}{' '}
                        Hash completo: <code>{published.hash}</code>
                    </p>
                )}

                <div className="cn-confirm-actions">
                    <button className="cn-btn" disabled={!text || localErrors.length > 0}
                        onClick={openLocalPreview}>
                        Pré-visualizar (sem publicar)
                    </button>
                    <button className="cn-btn" onClick={validate} disabled={!canSend || busy}>
                        {busy && !validOk ? 'Aguarde…' : 'Validar'}
                    </button>
                    <button className="cn-btn cn-btn-primary" onClick={publish}
                        disabled={!canSend || busy || !validOk || !!hashWarning}>
                        Publicar
                    </button>
                </div>
            </section>

            {preview && (
                <div className="cn-overlay" onClick={() => setPreview(null)}>
                    <div className="cn-modal cn-modal-wide" role="dialog" aria-modal="true" aria-labelledby="cn-preview-title"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="cn-modal-head">
                            <h2 id="cn-preview-title">{preview.title}</h2>
                        </div>
                        <div className="cn-modal-body">
                        <p className="cn-meta">
                            {preview.local ? 'Pré-visualização local: nada foi publicado.' : <>hash <code>{preview.hash}</code></>}
                            {' '}O visualizador roda o mesmo motor do app, mas não mostra o FPS, a GPU nem a háptica do aparelho.
                        </p>
                        {preview.loading && <p className="cn-empty">Carregando…</p>}
                        {preview.error && <p className="cn-error" role="alert">{preview.error}</p>}
                        {preview.body && (
                            <iframe ref={frameRef} className="cn-studio" title="Visualizador de cenas"
                                src="/scene-preview/?ui=admin" />
                        )}
                        </div>
                        <div className="cn-modal-foot">
                            <button className="cn-btn" onClick={() => setPreview(null)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}

            {viewer && (
                <div className="cn-overlay" onClick={() => setViewer(null)}>
                    <div className="cn-modal" role="dialog" aria-modal="true" aria-labelledby="cn-modal-title"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="cn-modal-head">
                            <h2 id="cn-modal-title">{viewer.scene.name}</h2>
                        </div>
                        <div className="cn-modal-body">
                        {viewer.loading && <p className="cn-empty">Carregando…</p>}
                        {viewer.error && <p className="cn-error" role="alert">{viewer.error}</p>}
                        {viewer.data && (
                            <>
                                <p className="cn-meta">
                                    {viewer.data.bytes} bytes · hash <code>{viewer.data.hash}</code>
                                    {viewer.data.deleted ? ' · retirada' : ''}
                                </p>
                                <div className="cn-confirm-actions">
                                    <button className="cn-btn cn-btn-sm" onClick={copyJson}>Copiar JSON</button>
                                </div>
                                <pre className="cn-json" tabIndex={0} aria-label="Corpo da cena (somente leitura)">{viewer.formatted}</pre>
                            </>
                        )}
                        </div>
                        <div className="cn-modal-foot">
                            <button className="cn-btn" ref={closeBtnRef} onClick={() => setViewer(null)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Cenas;
